import {
	Alert,
	AlertDescription,
	AlertIcon,
	Box,
	HStack,
	IconButton,
	StackDivider,
	Tooltip as ChakraTooltip,
	useToast,
	VStack,
} from "@chakra-ui/react";
import { RepeatIcon } from "@chakra-ui/icons";
import { chartColors } from "../../theme/chartColors";
import StatItem from "../../components/Shared/KPI/StatItem";
import Filter from "../../components/Shared/KPI/FIlter";
import { useEffect, useState } from "react";
import { FilterValue } from "../../models/KPI";
import { Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ComposedChart, Bar } from "recharts";
import MultipleSelectPopover from "../../components/MultipleSelectPopover";
import { t } from "i18next";
import {
	EconomyChartFigures,
	EconomyChartFiguresForDay,
	ResourceChartFiguresForDay,
	ResourcesChartFigures,
	ResourcesChartFiguresForYear,
} from "../../configs/KPI.config";
import { useKPIDashboard, useRefreshDashboardCache } from "../../networking/dashboard.hooks";
import styled from "styled-components";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { SectionListKeyValue } from "../../models/KeyValue";
import dayjs from "dayjs";
import { getWeekNumber } from "../../utils/date.time.utils";

interface Props {}

const KPI = (props: Props) => {
	const toast = useToast();
	const [value, setValue] = useState<FilterValue>({
		pool: "all",
		type: "date",
		startDate: dayjs().year().toString(),
		endDate: "",
		week: getWeekNumber(new Date()).toString(),
	});
	const [fetch, setFetch] = useState<boolean>(false);
	const [turnoverGrossProfit, setTurnoverGrossProfit] = useState<any>({
		turnOver: 0,
		grossProfit: 0,
		bookedShifts: 0,
		deniedShifts: 0,
		soldHours: 0,
		newApplications: 0,
		comparison: {
			turnOver: { display: "0.0%", value: 0 },
			grossProfit: { display: "0.0%", value: 0 },
			bookedShifts: { display: "0.0%", value: 0 },
			deniedShifts: { display: "0.0%", value: 0 },
			soldHours: { display: "0.0%", value: 0 },
			newApplications: { display: "0.0%", value: 0 },
		},
	});
	const [graphData, setGraphData] = useState([
		{
			name: "",
			cto: 0,
			pto: 0,
			cgp: 0,
			pgp: 0,
		},
	]);

	function generateComparisonPercentage(firstYear: number, secondYear: number) {
		if (firstYear === 0 && secondYear === 0) {
			return "0%";
		}
		if (firstYear === 0) {
			return "+100%";
		}
		const result = ((secondYear - firstYear) / firstYear) * 100;
		const fixedValue = result.toFixed(0);
		return parseInt(fixedValue, 10) + "%";
	}

	function generateComparisonValue(firstYear: number, secondYear: number) {
		if (firstYear === 0 && secondYear === 0) {
			return 0;
		}
		if (firstYear === 0) {
			return 100;
		}
		const result = ((secondYear - firstYear) / firstYear) * 100;
		const fixedValue = result.toFixed(2);
		return parseInt(fixedValue, 10);
	}

	const { mutate: refreshCache, isLoading: isRefreshing } = useRefreshDashboardCache();

	const handleRefreshCache = () => {
		refreshCache(
			{ clear: true },
			{
				onSuccess: () => {
					toast({
						id: "kpi-cache-refreshed",
						render: () => (
							<Alert status="success" borderWidth={2} borderRadius={4} variant={"solid"}>
								<AlertIcon />
								<AlertDescription>{t("DASHBOARD_CACHE_REFRESHED")}</AlertDescription>
							</Alert>
						),
						isClosable: true,
						position: "top",
						duration: 3000,
					});
					init(value);
				},
			}
		);
	};

	const {
		mutate: kpiMutate,
		isLoading,
		status,
	} = useKPIDashboard((response) => {
		try {
			setFetch(false);
			if (response) {
				setTurnoverGrossProfit({
					turnOver:
						value.type !== "year"
							? response?.revenue || 0
							: response?.breakdown[response?.breakdown.length - 1].current?.revenue || 0,
					grossProfit:
						value.type !== "year"
							? response?.grossProfit || 0
							: response?.breakdown[response?.breakdown.length - 1].current?.grossProfit || 0,
					bookedShifts:
						value.type !== "year"
							? response?.bookedShifts || 0
							: response?.breakdown[response?.breakdown.length - 1].current?.bookedShifts || 0,
					deniedShifts:
						value.type !== "year"
							? response?.deniedShifts || 0
							: response?.breakdown[response?.breakdown.length - 1].current?.deniedShifts || 0,
					soldHours:
						value.type !== "year"
							? response?.soldHours || 0
							: response?.breakdown[response?.breakdown.length - 1].current?.soldHours?.toFixed(
									2
							  ) || 0,
					newApplications:
						value.type !== "year"
							? response?.newApplications || 0
							: response?.breakdown[response?.breakdown.length - 1].current?.newApplications || 0,
					comparison: {
						turnOver: {
							display: generateComparisonPercentage(
								value.type !== "year"
									? response?.comparison.revenue
									: Array.isArray(response.breakdown) && response?.breakdown.length > 0
									? response.breakdown[0].current.revenue
									: 0,
								value.type !== "year"
									? response.revenue
									: Array.isArray(response.breakdown) && response?.breakdown.length > 0
									? response.breakdown[response.breakdown.length - 1].current.revenue
									: 0
							),
							value: generateComparisonValue(
								value.type !== "year"
									? response?.comparison.revenue
									: Array.isArray(response.breakdown) && response?.breakdown.length > 0
									? response.breakdown[0].current.revenue
									: 0,
								value.type !== "year"
									? response.revenue
									: Array.isArray(response.breakdown) && response?.breakdown.length > 0
									? response.breakdown[response.breakdown.length - 1].current.revenue
									: 0
							),
						},
						grossProfit: {
							display: generateComparisonPercentage(
								value.type !== "year"
									? response?.comparison.grossProfit
									: Array.isArray(response.breakdown) && response?.breakdown.length > 0
									? response.breakdown[0].current.grossProfit
									: 0,
								value.type !== "year"
									? response.grossProfit
									: Array.isArray(response.breakdown) && response?.breakdown.length > 0
									? response.breakdown[response.breakdown.length - 1].current.grossProfit
									: 0
							),
							value: generateComparisonValue(
								value.type !== "year"
									? response?.comparison.grossProfit
									: Array.isArray(response.breakdown) && response?.breakdown.length > 0
									? response.breakdown[0].current.grossProfit
									: 0,
								value.type !== "year"
									? response.grossProfit
									: Array.isArray(response.breakdown) && response?.breakdown.length > 0
									? response.breakdown[response.breakdown.length - 1].current.grossProfit
									: 0
							),
						},
						bookedShifts: {
							display: generateComparisonPercentage(
								value.type !== "year"
									? response?.comparison.bookedShifts
									: Array.isArray(response.breakdown) && response?.breakdown.length > 0
									? response.breakdown[0].current.bookedShifts
									: 0,
								value.type !== "year"
									? response.bookedShifts
									: Array.isArray(response.breakdown) && response?.breakdown.length > 0
									? response.breakdown[response.breakdown.length - 1].current.bookedShifts
									: 0
							),
							value: generateComparisonValue(
								value.type !== "year"
									? response?.comparison.bookedShifts
									: Array.isArray(response.breakdown) && response?.breakdown.length > 0
									? response.breakdown[0].current.bookedShifts
									: 0,
								value.type !== "year"
									? response.bookedShifts
									: Array.isArray(response.breakdown) && response?.breakdown.length > 0
									? response.breakdown[response.breakdown.length - 1].current.bookedShifts
									: 0
							),
						},
						deniedShifts: {
							display: generateComparisonPercentage(
								value.type !== "year"
									? response?.comparison.deniedShifts
									: Array.isArray(response.breakdown) && response?.breakdown.length > 0
									? response.breakdown[0].current.deniedShifts
									: 0,
								value.type !== "year"
									? response.deniedShifts
									: Array.isArray(response.breakdown) && response?.breakdown.length > 0
									? response.breakdown[response.breakdown.length - 1].current.deniedShifts
									: 0
							),
							value: generateComparisonValue(
								value.type !== "year"
									? response?.comparison.deniedShifts
									: Array.isArray(response.breakdown) && response?.breakdown.length > 0
									? response.breakdown[0].current.deniedShifts
									: 0,
								value.type !== "year"
									? response.deniedShifts
									: Array.isArray(response.breakdown) && response?.breakdown.length > 0
									? response.breakdown[response.breakdown.length - 1].current.deniedShifts
									: 0
							),
						},
						soldHours: {
							display: generateComparisonPercentage(
								value.type !== "year"
									? response?.comparison.soldHours
									: Array.isArray(response.breakdown) && response?.breakdown.length > 0
									? response.breakdown[0].current.soldHours
									: 0,
								value.type !== "year"
									? response.soldHours
									: Array.isArray(response.breakdown) && response?.breakdown.length > 0
									? response.breakdown[response.breakdown.length - 1].current.soldHours
									: 0
							),
							value: generateComparisonValue(
								value.type !== "year"
									? response?.comparison.soldHours
									: Array.isArray(response.breakdown) && response?.breakdown.length > 0
									? response.breakdown[0].current.soldHours
									: 0,
								value.type !== "year"
									? response.soldHours
									: Array.isArray(response.breakdown) && response?.breakdown.length > 0
									? response.breakdown[response.breakdown.length - 1].current.soldHours
									: 0
							),
						},
						newApplications: {
							display: generateComparisonPercentage(
								value.type !== "year"
									? response?.comparison.newApplications
									: Array.isArray(response.breakdown) && response?.breakdown.length > 0
									? response.breakdown[0].current.newApplications
									: 0,
								value.type !== "year"
									? response.newApplications
									: Array.isArray(response.breakdown) && response?.breakdown.length > 0
									? response.breakdown[response.breakdown.length - 1].current.newApplications
									: 0
							),
							value: generateComparisonValue(
								value.type !== "year"
									? response?.comparison.newApplications
									: Array.isArray(response.breakdown) && response?.breakdown.length > 0
									? response.breakdown[0].current.newApplications
									: 0,
								value.type !== "year"
									? response.newApplications
									: Array.isArray(response.breakdown) && response?.breakdown.length > 0
									? response.breakdown[response.breakdown.length - 1].current.newApplications
									: 0
							),
						},
					},
				});
			}
			if (response.breakdown && value.type === "month") {
				const revenue = response.breakdown.map((month: any) => {
					return {
						month: month.column,
						revenueCurrent: month.current.revenue,
						revenuePrevious: month.previous.revenue,
						grossProfitCurrent: month.current.grossProfit,
						grossProfitPrevious: month.previous.grossProfit,
						soldHoursCurrent: month.current.soldHours,
						bookedShiftsCurrent: month.current.bookedShifts,
						soldHoursPrevious: month.previous.soldHours,
						bookedShiftsPrevious: month.previous.bookedShifts,
						workingConsultantsCurrent: month.current.workingConsultant,
						workingConsultantsPrevious: month.previous.workingConsultant,
						activeConsultantsCurrent: month.current.activeConsultant,
						activeConsultantsPrevious: month.previous.activeConsultant,
						bookedPassesCurrent: month.current.bookedShifts,
						bookedPassesPrevious: month.previous.bookedShifts,
						hoursSoldCurrent: month.current.soldHours,
						hoursSoldPrevious: month.previous.soldHours,
						deniedShiftCurrent: month.current.deniedShifts,
						deniedShiftPrevious: month.previous.deniedShifts,
						newApplicationsCurrent: month.current.newApplications,
						newApplicationsPrevious: month.previous.newApplications,
						newlyEmployedConsultantsCurrent: month.current.newApplications,
						newlyEmployedConsultantsPrevious: month.previous.newApplications,
					};
				});
				if (value.type === "month") {
					const revenueTempArray =
						Array.isArray(revenue) &&
						revenue.map((rev) => {
							return {
								name: rev.month,
								cto: rev.revenueCurrent,
								pto: rev.revenuePrevious,
								cgp: rev.grossProfitCurrent,
								pgp: rev.grossProfitPrevious,
								wcc: rev.workingConsultantsCurrent,
								wcp: rev.workingConsultantsPrevious,
								wcc_wcp: (
									parseInt(rev.workingConsultantsCurrent, 10) +
									parseInt(rev.workingConsultantsPrevious, 10)
								).toString(),
								acc: rev.activeConsultantsCurrent,
								acp: rev.activeConsultantsPrevious,
								acc_acp: (
									parseInt(rev.activeConsultantsCurrent, 10) +
									parseInt(rev.activeConsultantsPrevious, 10)
								).toString(),
								bpc: rev.bookedPassesCurrent,
								bpp: rev.bookedPassesPrevious,
								bpc_bpp: (
									parseInt(rev.bookedPassesCurrent, 10) + parseInt(rev.bookedPassesPrevious, 10)
								).toString(),
								hsc: rev.hoursSoldCurrent,
								hsp: rev.hoursSoldPrevious,
								hsc_hsp: (
									parseInt(rev.hoursSoldCurrent, 10) + parseInt(rev.hoursSoldPrevious, 10)
								).toString(),
								dsc: rev.bookedPassesCurrent,
								dnp: rev.bookedPassesPrevious,
								dsc_dnp: (
									parseInt(rev.deniedShiftCurrent, 10) + parseInt(rev.deniedShiftPrevious, 10)
								).toString(),
								nac: rev.newApplicationsCurrent,
								nap: rev.newApplicationsPrevious,
								nac_nap: (
									parseInt(rev.newApplicationsCurrent, 10) +
									parseInt(rev.newApplicationsPrevious, 10)
								).toString(),
								nec: rev.newlyEmployedConsultantsCurrent,
								nep: rev.newlyEmployedConsultantsPrevious,
								nec_nep: (
									parseInt(rev.newlyEmployedConsultantsCurrent, 10) +
									parseInt(rev.newlyEmployedConsultantsPrevious, 10)
								).toString(),
							};
						});
					Array.isArray(revenueTempArray) ? setGraphData(revenueTempArray) : setGraphData([]);

					const economyChart = [...economyChartFiguresForDay];
					const tempEconomyChart =
						Array.isArray(economyChart) &&
						economyChart.map((chart) => {
							if (chart.header === "Omsättning") {
								return {
									...chart,
									value: [
										{ key: "Omsättning current", value: value.startDate },
										{ key: "Omsättning previous", value: parseInt(value.startDate, 10) - 1 },
									],
								};
							} else if (chart.header === "Bruttovinst") {
								return {
									...chart,
									value: [
										{ key: "Bruttovinst current", value: value.startDate },
										{ key: "Bruttovinst previous", value: parseInt(value.startDate, 10) - 1 },
									],
								};
							}
						});
					setEconomyChartFiguresForDay(
						(tempEconomyChart as Array<SectionListKeyValue>) || economyChartFiguresForDay
					);

					const resourceChart = [...resourceChartFigures];
					const tempResourceChart =
						Array.isArray(resourceChart) &&
						resourceChart.map((chart) => {
							if (chart.header === "Arbetande konsulter") {
								return {
									...chart,
									value: [
										{ key: "Arbetande konsulter current", value: value.startDate },
										{
											key: "Arbetande konsulter previous",
											value: parseInt(value.startDate, 10) - 1,
										},
									],
								};
							} else if (chart.header === "Aktiva konsulter") {
								return {
									...chart,
									value: [
										{ key: "Aktiva konsulter current", value: value.startDate },
										{ key: "Aktiva konsulter previous", value: parseInt(value.startDate, 10) - 1 },
									],
								};
							} else if (chart.header === "Bokade pass") {
								return {
									...chart,
									value: [
										{ key: "Bokade pass current", value: value.startDate },
										{ key: "Bokade pass previous", value: parseInt(value.startDate, 10) - 1 },
									],
								};
							} else if (chart.header === "Nekade pass") {
								return {
									...chart,
									value: [
										{ key: "Nekade pass current", value: value.startDate },
										{ key: "Nekade pass previous", value: parseInt(value.startDate, 10) - 1 },
									],
								};
							} else if (chart.header === "Ansökningar") {
								return {
									...chart,
									value: [
										{ key: "Ansökningar current", value: value.startDate },
										{ key: "Ansökningar previous", value: parseInt(value.startDate, 10) - 1 },
									],
								};
							} else if (chart.header === "Nyanställda") {
								return {
									...chart,
									value: [
										{ key: "Nyanställda current", value: value.startDate },
										{ key: "Nyanställda previous", value: parseInt(value.startDate, 10) - 1 },
									],
								};
							} else {
								return {
									...chart,
									value: [
										{ key: "Sålda timmar current", value: value.startDate },
										{ key: "Sålda timmar previous", value: parseInt(value.startDate, 10) - 1 },
									],
								};
							}
						});

					setResourceChartFigures(
						(tempResourceChart as Array<SectionListKeyValue>) || ResourcesChartFigures
					);
				}
			} else if (value.type === "year") {
				const revenueTempArray =
					Array.isArray(response.breakdown) &&
					response.breakdown.map((rev: any) => {
						return {
							name: rev.column,
							Bruttovinst: rev?.current?.grossProfit || 0,
							Omsättning: rev?.current?.revenue || 0,
							GenomsnittligKostna: rev?.current?.averageCost || 0,
							GenomsnittligtPris: rev?.current?.averagePrice || 0,
							bookedShifts: rev?.current?.bookedShifts || 0,
							deniedShifts: rev?.current?.deniedShifts || 0,
							soldHours: rev?.current?.soldHours || 0,
							newApplications: rev?.current?.newApplications || 0,
							newlyEmployedConsultant: rev?.current?.newlyEmployedConsultant || 0,
							activeConsultant: rev?.current?.activeConsultant || 0,
							workingConsultant: rev?.current?.workingConsultant || 0,
						};
					});
				Array.isArray(revenueTempArray) ? setGraphData(revenueTempArray) : setGraphData([]);
			} else if (value.type === "date") {
				const revenueTempArray =
					Array.isArray(response.breakdown) &&
					response.breakdown.map((rev: any) => {
						return {
							name: rev.column,
							trunover: rev.current.revenue,
							turnOverPrevious: rev.previous.revenue || 0,
							grossProfitCurrent: rev.current.grossProfit,
							grossProfitPrevious: rev.previous.grossProfit,
							hoursSoldCurrent: rev.current.soldHours,
							hoursSoldPrevious: rev.previous.soldHours,
							bookedShiftsCurrent: rev.current.bookedShifts,
							bookedShiftsPrevious: rev.previous.soldHours,
						};
					});
				Array.isArray(revenueTempArray) ? setGraphData(revenueTempArray) : setGraphData([]);

				const economyChart = [...economyChartFiguresForDay];
				const tempEconomyChart =
					Array.isArray(economyChart) &&
					economyChart.map((chart) => {
						if (chart.header === "Omsättning") {
							return {
								...chart,
								value: [
									{ key: "Omsättning current", value: value.startDate },
									{ key: "Omsättning previous", value: parseInt(value.startDate, 10) - 1 },
								],
							};
						} else if (chart.header === "Bruttovinst") {
							return {
								...chart,
								value: [
									{ key: "Bruttovinst current", value: value.startDate },
									{ key: "Bruttovinst previous", value: parseInt(value.startDate, 10) - 1 },
								],
							};
						}
					});
				setEconomyChartFiguresForDay(
					(tempEconomyChart as Array<SectionListKeyValue>) || economyChartFiguresForDay
				);

				const resourceChart = [...resourceChartFiguresForDay];
				const tempResourceChart =
					Array.isArray(resourceChart) &&
					resourceChart.map((chart) => {
						if (chart.header === "Sålda timmar") {
							return {
								...chart,
								value: [
									{ key: "Sålda timmar current", value: value.startDate },
									{ key: "Sålda timmar previous", value: parseInt(value.startDate, 10) - 1 },
								],
							};
						} else if (chart.header === "Bokade pass") {
							return {
								...chart,
								value: [
									{ key: "Bokade pass current", value: value.startDate },
									{ key: "Bokade pass previous", value: parseInt(value.startDate, 10) - 1 },
								],
							};
						}
					});
				setResourceChartFiguresForDay(
					(tempResourceChart as Array<SectionListKeyValue>) || resourceChartFiguresForDay
				);
			}
		} catch (error) {
			toast({
				id: "KPI dashboard loading error",
				render: () => (
					<Alert status="error" borderWidth={2} borderRadius={4} variant={"solid"}>
						<AlertDescription>{t("SOMETHING_WENT_WRONG")}</AlertDescription>
					</Alert>
				),
				isClosable: true,
				position: "bottom-right",
				duration: 3 * 1000,
			});
		}
	});

	useEffect(() => {
		if (value && !isLoading) {
			init(value);
			return () => {};
		}
	}, [value]);

	useEffect(() => {
		if (isLoading) {
			const timeout = setTimeout(() => {
				if (isLoading) {
					toast({
						id: "laddar datan",
						render: () => (
							<Alert status="loading" borderWidth={2} borderRadius={4} variant={"solid"}>
								<AlertIcon />
								<AlertDescription>{` laddar datan...`}</AlertDescription>
							</Alert>
						),
						isClosable: true,
						position: "top",
						duration: 3 * 1000,
					});
				}
			}, 3000);

			return () => clearTimeout(timeout);
		}
	}, [isLoading]);

	async function init(data: FilterValue) {
		if (data.type === "month") {
			if (data.pool !== "all") {
				kpiMutate({
					pool: data.pool,
					type: data.type,
					year: data.startDate,
				});
			} else {
				kpiMutate({
					type: data.type,
					year: data.startDate,
				});
			}
		} else if (data.type === "year") {
			if (data.pool !== "all") {
				kpiMutate({
					pool: data.pool,
					type: data.type,
					year: data.startDate,
					toYear: data.week,
				});
			} else {
				kpiMutate({
					type: data.type,
					year: data.startDate,
					toYear: data.week,
				});
			}
		} else if (data.type === "date") {
			if (data.pool !== "all") {
				kpiMutate({
					pool: data.pool,
					type: data.type,
					year: data.startDate,
					week: data.week,
				});
			} else {
				kpiMutate({
					type: data.type,
					year: data.startDate,
					week: data.week,
				});
			}
		}
	}

	const [selectedEconomyChartFigures, setSelectedEconomyChartFigures] = useState<string[]>([
		"Omsättning",
		"Bruttovinst",
		"Genomsnittlig kostna",
		"Genomsnittligt pris",
	]);

	const [selectedResourceChartFigures, setSelectedResourceChartFigures] = useState<string[]>([
		"Arbetande konsulter current",
		"Aktiva konsulter current",
		"Sålda timmar current",
		"Bokade pass current",
	]);

	const [selectedResourceChartFiguresForYear, setSelectedResourceChartFiguresForYear] = useState<
		string[]
	>([]);

	const [selectedEconomyChartFiguresForDay, setSelectedEconomyChartFiguresForDay] = useState<
		string[]
	>(["Omsättning current", "Omsättning previous", "Bruttovinst current", "Bruttovinst previous"]);

	const [selectedEconomyChartFiguresForMonth, setSelectedEconomyChartFiguresForMonth] = useState<
		string[]
	>(["Omsättning current", "Omsättning previous", "Bruttovinst current", "Bruttovinst previous"]);

	const [selectedResourceChartFiguresForDay, setSelectedResourceChartFiguresForDay] = useState<
		string[]
	>([
		"Sålda timmar current",
		"Bokade pass current",
		"Sålda timmar previous",
		"Bokade pass previous",
	]);

	const [resourceChartFigures, setResourceChartFigures] =
		useState<Array<SectionListKeyValue>>(ResourcesChartFigures);

	const [economyChartFiguresForDay, setEconomyChartFiguresForDay] =
		useState<Array<SectionListKeyValue>>(EconomyChartFiguresForDay);

	const [resourceChartFiguresForDay, setResourceChartFiguresForDay] = useState<
		Array<SectionListKeyValue>
	>(ResourceChartFiguresForDay);

	const CustomTooltip = (param: any) => {
		const tooltipStyle = {
			backgroundColor: "rgba(51, 51, 51, 0.6)", // 80% opacity
			color: "#fff",
			padding: "10px",
			fontSize: "16px",
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
			width: "200px",
		};

		const textStyle = {
			margin: "0 10px 0 0",
			fontSize: "14px",
			opacity: 0.7,
		};

		const valueStyle = {
			fontSize: "24px",
			fontWeight: "bold",
		};

		return (
			Array.isArray(param.payload) &&
			param.payload.map((item: any) => (
				<div style={tooltipStyle}>
					<div>
						<p style={textStyle}>{item.name}</p>
						<div style={{ display: "flex", alignItems: "center" }}>
							<div
								style={{
									backgroundColor: item.color,
									width: "15px",
									height: "15px",
									borderRadius: "2px",
									marginRight: "10px",
								}}
							></div>
							{item.value.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
						</div>
					</div>
				</div>
			))
		);
	};

	return (
		<VStack
			divider={<StackDivider borderColor="green" />}
			spacing={4}
			align="stretch"
			className="p-4 px-6"
		>
			<HStack justify="space-between" align="center" w="100%">
				<Filter
					value={value}
					onChange={setValue}
					onFetch={setFetch}
					fetch={fetch}
					isLoading={isLoading}
				/>
				<ChakraTooltip label={t("DASHBOARD_REFRESH_CACHE_TOOLTIP")} placement="left" hasArrow>
					<IconButton
						aria-label={t("DASHBOARD_REFRESH_CACHE_TOOLTIP") || "Refresh dashboard cache"}
						icon={<RepeatIcon />}
						size="sm"
						variant="ghost"
						color="#9A9A9A"
						_hover={{ color: "#4F4F4F", bg: "transparent" }}
						isLoading={isRefreshing}
						onClick={handleRefreshCache}
					/>
				</ChakraTooltip>
			</HStack>
			<VStack gap={5}>
				<HStack width={"100%"} gap={5}>
					<Box
						boxShadow={"0px 2px 11px 0px #00000026"}
						borderRadius={"8px"}
						borderColor={"#D4D4D4"}
						w="70%"
						p={4}
						h={"345px"}
					>
						<HStack justify={"space-between"}>
							<ChartHeaderText>
								<div className="chart-title-text-style">{"Ekonomi"}</div>
							</ChartHeaderText>
							{value.type === "year" ? (
								<MultipleSelectPopover
									label={t("STATUS")}
									// className="full-width"
									popoverWidth={200}
									selectedValues={selectedEconomyChartFigures}
									options={EconomyChartFigures}
									onSelect={(val) => {
										if (selectedEconomyChartFigures.includes(val as string)) {
											setSelectedEconomyChartFigures((prev) => {
												const newList: string[] = prev.filter((s) => s !== val);
												return newList;
											});
										} else {
											setSelectedEconomyChartFigures((prev) => {
												const newList: string[] = [...prev, val as string];
												return newList;
											});
										}
									}}
									popoverPosition="left"
								/>
							) : (
								(value.type === "date" || value.type === "month") && (
									<MultipleSelectPopover
										label={t("STATUS")}
										// className="full-width"
										popoverWidth={200}
										selectedValues={selectedEconomyChartFiguresForDay}
										options={EconomyChartFigures}
										sectionList={true}
										sectionListOption={
											value.type === "date" ? economyChartFiguresForDay : economyChartFiguresForDay
										}
										onSelect={(val) => {
											if (selectedEconomyChartFiguresForDay.includes(val as string)) {
												setSelectedEconomyChartFiguresForDay((prev) => {
													const newList: string[] = prev.filter((s) => s !== val);
													return newList;
												});
											} else {
												setSelectedEconomyChartFiguresForDay((prev) => {
													const newList: string[] = [...prev, val as string];
													return newList;
												});
											}
										}}
										popoverPosition="left"
									/>
								)
							)}
						</HStack>
						{isLoading ? (
							<Skeleton height={250} count={1} />
						) : value.type === "month" ? (
							<ComposedChart style={{ marginTop: 20 }} width={840} height={250} data={graphData}>
								<XAxis dataKey="name" tick={{ fontSize: 10 }} />
								<YAxis tick={{ fontSize: 10 }} />
								<Tooltip
									content={(param) => (
										<CustomTooltip
											active={param.active}
											payload={param.payload}
											label={param.label}
										/>
									)}
								/>
								<Legend wrapperStyle={{ fontSize: "10px" }} />
								<CartesianGrid stroke="#f5f5f5" />
								{selectedEconomyChartFiguresForDay.includes("Bruttovinst current") && (
									<Bar
										name={"Bruttovinst (" + parseInt(value?.startDate, 10) + ")"}
										barSize={8}
										dataKey="cgp"
										fill={chartColors.action}
									/>
								)}
								{selectedEconomyChartFiguresForDay.includes("Bruttovinst previous") && (
									<Bar
										name={"Bruttovinst (" + (parseInt(value?.startDate, 10) - 1) + ")"}
										barSize={8}
										dataKey="pgp"
										fill="#b0d6ea"
									/>
								)}
								{selectedEconomyChartFiguresForDay.includes("Omsättning current") && (
									<Line
										name={"Omsättning (" + parseInt(value?.startDate, 10) + ")"}
										type="linear"
										dataKey="cto"
										stroke={chartColors.brand}
										strokeWidth={2}
									/>
								)}
								{selectedEconomyChartFiguresForDay.includes("Omsättning previous") && (
									<Line
										name={"Omsättning (" + (parseInt(value?.startDate, 10) - 1) + ")"}
										type="linear"
										strokeDasharray="3 3"
										dataKey="pto"
										stroke="#b0dab7"
										strokeWidth={2}
									/>
								)}
							</ComposedChart>
						) : value.type === "year" ? (
							<ComposedChart style={{ marginTop: 20 }} width={840} height={250} data={graphData}>
								<XAxis dataKey="name" tick={{ fontSize: 10 }} />
								<YAxis tick={{ fontSize: 10 }} />
								<Tooltip
									content={(param) => (
										<CustomTooltip
											active={param.active}
											payload={param.payload}
											label={param.label}
										/>
									)}
								/>
								<Legend wrapperStyle={{ fontSize: "10px" }} />
								<CartesianGrid stroke="#f5f5f5" />
								{selectedEconomyChartFigures.includes("Bruttovinst") && (
									<Bar
										name={"Bruttovinst"}
										barSize={8}
										dataKey="Bruttovinst"
										fill={chartColors.action}
									/>
								)}
								{selectedEconomyChartFigures.includes("Omsättning") && (
									<Line
										name={"Omsättning"}
										type="linear"
										dataKey="Omsättning"
										stroke={chartColors.brand}
										strokeWidth={2}
									/>
								)}
								{selectedEconomyChartFigures.includes("Genomsnittlig kostna") && (
									<Bar
										name={"Genomsnittlig kostna"}
										barSize={5}
										dataKey="GenomsnittligKostna"
										fill={chartColors.danger}
									/>
								)}
								{selectedEconomyChartFigures.includes("Genomsnittligt pris") && (
									<Line
										name={"Genomsnittligt pris"}
										type="linear"
										strokeDasharray="3 3"
										dataKey="GenomsnittligtPris"
										stroke="#E79B38"
										strokeWidth={2}
									/>
								)}
							</ComposedChart>
						) : value.type === "date" ? (
							<ComposedChart style={{ marginTop: 20 }} width={840} height={250} data={graphData}>
								<XAxis dataKey="name" tick={{ fontSize: 10 }} />
								<YAxis tick={{ fontSize: 10 }} />
								<Tooltip
									content={(param) => (
										<CustomTooltip
											active={param.active}
											payload={param.payload}
											label={param.label}
										/>
									)}
								/>
								<Legend wrapperStyle={{ fontSize: "10px" }} />
								<CartesianGrid stroke="#f5f5f5" />
								{selectedEconomyChartFiguresForDay.includes("Bruttovinst current") && (
									<Bar
										name={"Bruttovinst (" + value.startDate + ")"}
										barSize={8}
										dataKey="grossProfitCurrent"
										fill={chartColors.action}
									/>
								)}
								{selectedEconomyChartFiguresForDay.includes("Bruttovinst previous") && (
									<Bar
										name={"Bruttovinst (" + (parseInt(value?.startDate, 10) - 1) + ")"}
										dataKey="grossProfitPrevious"
										barSize={8}
										fill="#b0d6ea"
									/>
								)}
								{selectedEconomyChartFiguresForDay.includes("Omsättning current") && (
									<Line
										name={"Omsättning  (" + value.startDate + ")"}
										type="linear"
										dataKey="trunover"
										stroke={chartColors.brand}
										strokeWidth={2}
									/>
								)}
								{selectedEconomyChartFiguresForDay.includes("Omsättning previous") && (
									<Line
										name={"Omsättning (" + (parseInt(value?.startDate, 10) - 1) + ")"}
										strokeDasharray="3 3"
										type="linear"
										dataKey="turnOverPrevious"
										stroke="#b0dab7"
										strokeWidth={2}
									/>
								)}
							</ComposedChart>
						) : (
							<></>
						)}
					</Box>

					<VStack w="30%" gap={5}>
						<StatItem
							title={"Omsättning"}
							count={turnoverGrossProfit.turnOver}
							tagContent={turnoverGrossProfit.comparison.turnOver.display}
							noteText="vs. föregående år"
							tagBackgroundColor={
								turnoverGrossProfit.comparison.turnOver.value < 0
									? chartColors.danger
									: chartColors.brand
							}
							isCurrencyValue
							isLoading={isLoading}
						/>

						<StatItem
							title={"Bruttovinst"}
							count={turnoverGrossProfit.grossProfit}
							tagContent={turnoverGrossProfit.comparison.grossProfit.display}
							noteText="vs. föregående år"
							tagBackgroundColor={
								turnoverGrossProfit.comparison.grossProfit.value < 0
									? chartColors.danger
									: chartColors.brand
							}
							isCurrencyValue
							isLoading={isLoading}
						/>
					</VStack>
				</HStack>

				<HStack width={"100%"} gap={5}>
					<Box
						boxShadow={"0px 2px 11px 0px #00000026"}
						borderRadius={"8px"}
						borderColor={"#D4D4D4"}
						w="70%"
						p={4}
						h={"345px"}
					>
						<HStack justify={"space-between"}>
							<ChartHeaderText>
								<div className="chart-title-text-style">{"Resurser"}</div>
							</ChartHeaderText>
							{value.type === "month" && (
								<MultipleSelectPopover
									label={t("STATUS")}
									// className="full-width"
									popoverWidth={200}
									sectionList={true}
									selectedValues={selectedResourceChartFigures}
									options={EconomyChartFigures}
									sectionListOption={resourceChartFigures}
									onSelect={(val) => {
										if (selectedResourceChartFigures.includes(val as string)) {
											setSelectedResourceChartFigures((prev) => {
												const newList: string[] = prev.filter((s) => s !== val);
												return newList;
											});
										} else {
											setSelectedResourceChartFigures((prev) => {
												const newList: string[] = [...prev, val as string];
												return newList;
											});
										}
									}}
									popoverPosition="left"
								/>
							)}
							{value.type === "year" ? (
								<MultipleSelectPopover
									label={t("STATUS")}
									// className="full-width"
									popoverWidth={200}
									selectedValues={selectedResourceChartFiguresForYear}
									options={ResourcesChartFiguresForYear}
									onSelect={(val) => {
										if (selectedResourceChartFiguresForYear.includes(val as string)) {
											setSelectedResourceChartFiguresForYear((prev) => {
												const newList: string[] = prev.filter((s) => s !== val);
												return newList;
											});
										} else {
											setSelectedResourceChartFiguresForYear((prev) => {
												const newList: string[] = [...prev, val as string];
												return newList;
											});
										}
									}}
									popoverPosition="left"
								/>
							) : (
								value.type === "date" && (
									<MultipleSelectPopover
										label={t("STATUS")}
										// className="full-width"
										popoverWidth={200}
										selectedValues={selectedResourceChartFiguresForDay}
										options={EconomyChartFigures}
										sectionList={true}
										sectionListOption={resourceChartFiguresForDay}
										onSelect={(val) => {
											if (selectedResourceChartFiguresForDay.includes(val as string)) {
												setSelectedResourceChartFiguresForDay((prev) => {
													const newList: string[] = prev.filter((s) => s !== val);
													return newList;
												});
											} else {
												setSelectedResourceChartFiguresForDay((prev) => {
													const newList: string[] = [...prev, val as string];
													return newList;
												});
											}
										}}
										popoverPosition="left"
									/>
								)
							)}
						</HStack>
						{isLoading ? (
							<Skeleton height={250} count={1} />
						) : value.type === "month" ? (
							<ComposedChart style={{ marginTop: 20 }} width={840} height={250} data={graphData}>
								<XAxis dataKey="name" tick={{ fontSize: 10 }} />
								<YAxis tick={{ fontSize: 10 }} />
								<Tooltip
									content={(param) => (
										<CustomTooltip
											active={param.active}
											payload={param.payload}
											label={param.label}
										/>
									)}
								/>
								<Legend wrapperStyle={{ fontSize: "10px" }} />
								<CartesianGrid stroke="#f5f5f5" />
								{selectedResourceChartFigures.includes("Arbetande konsulter current") && (
									<Bar
										name={"Arbetande konsulter (" + parseInt(value?.startDate, 10) + ")"}
										barSize={8}
										dataKey="wcc"
										fill={chartColors.danger}
									/> // working consultants
								)}

								{selectedResourceChartFigures.includes("Arbetande konsulter previous") && (
									<Bar
										name={"Arbetande konsulter (" + (parseInt(value?.startDate, 10) - 1) + ")"}
										barSize={8}
										dataKey="wcp"
										fill="#f59898"
									/> // working consultants
								)}

								{selectedResourceChartFigures.includes("Aktiva konsulter current") && (
									<Bar
										name={"Aktiva konsulter (" + parseInt(value?.startDate, 10) + ")"}
										barSize={8}
										dataKey="acc"
										fill="#E79B38"
									/> // active consultants
								)}

								{selectedResourceChartFigures.includes("Aktiva konsulter previous") && (
									<Bar
										name={"Aktiva konsulter (" + (parseInt(value?.startDate, 10) - 1) + ")"}
										barSize={8}
										dataKey="acp"
										fill="#e8bd84"
									/> // active consultants
								)}
								{selectedResourceChartFigures.includes("Sålda timmar current") && (
									<Line
										name={"Sålda timmar (" + parseInt(value?.startDate, 10) + ")"}
										type="linear"
										dataKey="hsc"
										stroke={chartColors.brand}
										strokeWidth={2}
									/> // sold hours curve
								)}
								{selectedResourceChartFigures.includes("Sålda timmar previous") && (
									<Line
										name={"Sålda timmar (" + (parseInt(value?.startDate, 10) - 1) + ")"}
										type="linear"
										dataKey="hsp"
										stroke="#b0dab7"
										strokeWidth={2}
									/> // sold hours curve
								)}
								{selectedResourceChartFigures.includes("Bokade pass current") && (
									<Bar
										name={"Bokade pass(" + parseInt(value?.startDate, 10) + ")"}
										barSize={8}
										dataKey="bpc"
										fill={chartColors.action}
									/> // booked shifts
								)}
								{selectedResourceChartFigures.includes("Bokade pass previous") && (
									<Bar
										name={"Bokade pass(" + (parseInt(value?.startDate, 10) - 1) + ")"}
										barSize={8}
										dataKey="bpp"
										fill="#b0d6ea"
									/> // booked shifts
								)}
							</ComposedChart>
						) : value.type === "year" ? (
							<ComposedChart style={{ marginTop: 20 }} width={840} height={250} data={graphData}>
								<XAxis dataKey="name" tick={{ fontSize: 10 }} />
								<YAxis tick={{ fontSize: 10 }} />
								<Tooltip
									content={(param) => (
										<CustomTooltip
											active={param.active}
											payload={param.payload}
											label={param.label}
										/>
									)}
								/>
								<Legend wrapperStyle={{ fontSize: "10px" }} />
								<CartesianGrid stroke="#f5f5f5" />
								{selectedResourceChartFiguresForYear.includes("Bokade pass") && (
									<Bar
										name={"Bokade pass"}
										barSize={8}
										dataKey="bookedShifts"
										fill={chartColors.action}
									/>
								)}
								{selectedResourceChartFiguresForYear.includes("Nekade pass") && (
									<Bar name={"Nekade pass"} barSize={8} dataKey="deniedShifts" fill="#141414" />
								)}
								{selectedResourceChartFiguresForYear.includes("Ansökningar") && (
									<Bar name={"Ansökningar"} barSize={8} dataKey="newApplications" fill="#003DDA" />
								)}
								{selectedResourceChartFiguresForYear.includes("Nyanställda") && (
									<Bar
										name={"Nyanställda"}
										barSize={8}
										dataKey="newlyEmployedConsultant"
										fill="#B357EB"
									/>
								)}
								{selectedResourceChartFiguresForYear.includes("Aktiva konsulter") && (
									<Bar
										name={"Aktiva konsulter"}
										barSize={8}
										dataKey="activeConsultant"
										fill="#E79B38"
									/>
								)}
								{selectedResourceChartFiguresForYear.includes("Arbetande konsulter") && (
									<Bar
										name={"Arbetande konsulter"}
										barSize={8}
										dataKey="workingConsultant"
										fill={chartColors.danger}
									/>
								)}
								{selectedResourceChartFiguresForYear.includes("Sålda timmar") && (
									<Line
										name={"Sålda timmar"}
										type="linear"
										dataKey="soldHours"
										stroke={chartColors.brand}
										strokeWidth={2}
									/>
								)}
							</ComposedChart>
						) : (
							value.type === "date" && (
								<ComposedChart style={{ marginTop: 20 }} width={840} height={250} data={graphData}>
									<XAxis dataKey="name" tick={{ fontSize: 10 }} />
									<YAxis tick={{ fontSize: 10 }} />
									<Tooltip
										content={(param) => (
											<CustomTooltip
												active={param.active}
												payload={param.payload}
												label={param.label}
											/>
										)}
									/>
									<Legend wrapperStyle={{ fontSize: "10px" }} />
									<CartesianGrid stroke="#f5f5f5" />
									{selectedResourceChartFiguresForDay.includes("Bokade pass current") && (
										<Bar
											name={"Bokade pass"}
											barSize={8}
											dataKey="bookedShiftsCurrent"
											fill={chartColors.action}
										/>
									)}
									{selectedResourceChartFiguresForDay.includes("Bokade pass previous") && (
										<Bar
											name={"Bokade pass (" + (parseInt(value.startDate, 10) - 1) + ")"}
											barSize={8}
											dataKey="bookedShiftsPrevious"
											fill="#b0d6ea"
										/>
									)}
									{selectedResourceChartFiguresForDay.includes("Sålda timmar current") && (
										<Line
											name={"Sålda timmar"}
											type="linear"
											dataKey="hoursSoldCurrent"
											stroke={chartColors.brand}
											strokeWidth={2}
										/>
									)}
									{selectedResourceChartFiguresForDay.includes("Sålda timmar previous") && (
										<Line
											name={"Sålda timmar (" + (parseInt(value.startDate, 10) - 1) + ")"}
											type="linear"
											dataKey="hoursSoldPrevious"
											strokeDasharray="3 3"
											stroke="#b0dab7"
											strokeWidth={2}
										/>
									)}
								</ComposedChart>
							)
						)}
					</Box>
					<VStack w="30%" gap={5}>
						<HStack width={"100%"} gap={5}>
							<StatItem
								title={"Bokade pass"}
								count={turnoverGrossProfit.bookedShifts}
								tagContent={turnoverGrossProfit.comparison.bookedShifts.display}
								noteText="vs. föregående år"
								tagBackgroundColor={
									turnoverGrossProfit.comparison.bookedShifts.value < 0
										? chartColors.danger
										: chartColors.brand
								}
								isLoading={isLoading}
							/>
							<StatItem
								title={"Nekade pass"}
								count={turnoverGrossProfit.deniedShifts}
								tagContent={turnoverGrossProfit.comparison.deniedShifts.display}
								noteText="vs. föregående år"
								tagBackgroundColor={
									turnoverGrossProfit.comparison.deniedShifts.value < 0
										? chartColors.danger
										: chartColors.brand
								}
								isLoading={isLoading}
							/>
						</HStack>
						<HStack width={"100%"} gap={5}>
							<StatItem
								title={"Ansökningar"}
								count={turnoverGrossProfit.newApplications}
								tagContent={turnoverGrossProfit.comparison.newApplications.display}
								noteText="vs. föregående år"
								tagBackgroundColor={
									turnoverGrossProfit.comparison.newApplications.value < 0
										? chartColors.danger
										: chartColors.brand
								}
								isLoading={isLoading}
							/>
							<StatItem
								title={"Sålda timmar"}
								count={turnoverGrossProfit.soldHours}
								tagContent={turnoverGrossProfit.comparison.soldHours.display}
								tagBackgroundColor={
									turnoverGrossProfit.comparison.soldHours.value < 0
										? chartColors.danger
										: chartColors.brand
								}
								noteText="vs. föregående år"
								isLoading={isLoading}
								decimalLocalizationEnabled={true}
							/>
						</HStack>
					</VStack>
				</HStack>
			</VStack>
		</VStack>
	);
};
export default KPI;

const ChartHeaderText = styled.div<{ margin?: string }>`
	.chart-title-text-style {
		font-size: 22px; /* Adjust based on the size in the image */
		color: #4f4f4f; /* Gray color */
		font-weight: 400; /* Normal weight */
	}
`;
